/**
 * ============================== SWAP IN POINT ==============================
 * As of the Aug 20 2026 backend update, Patient/GetAllDoctorsAllTimeSlots
 * returns a `BookingLink` per doctor - already resolved server-side (initial
 * vs follow-up decided by checking this patient's Appointment history) - and
 * `HasSufficientPoints`. See clinicianDirectory.js, which now reads both off
 * the API response as `bookingLink`/`hasSufficientPoints` on each clinician.
 *
 * The static per-provider table below (CareProviderList.txt, Aug 2026) is
 * kept commented out for reference / as a rollback path, but is no longer
 * live - the backend is the source of truth now.
 * =============================================================================
 */

// function normalizeName(value) {
//   return String(value || "")
//     .trim()
//     .toLowerCase()
//     .replace(/\s+/g, " ");
// }
//
// // Keyed by normalized "First Last" - matches clinicianDirectory.js's `name`
// // field, which comes from Patient/GetAllDoctorsAllTimeSlots (u.FirstName + ' ' + u.LastName).
// const SCHEDULER_LINKS_BY_NAME = {
//   "anita gill": {
//     initialBookingUrl: "https://scheduler.zoom.us/anita-gill-1/initial-clinician-inperson",
//     followUpBookingUrl: "https://scheduler.zoom.us/anita-gill-1/followup-clinician",
//   },
//   "crisia tabacaru": {
//     initialBookingUrl: "https://scheduler.zoom.us/crisia-tabacaru/initial-fertility-doula",
//     followUpBookingUrl: "https://scheduler.zoom.us/crisia-tabacaru/followup-fertility-doula",
//   },
//   "megan murray": {
//     initialBookingUrl: "https://scheduler.zoom.us/megan-murray-1/initial-nutritionist",
//     followUpBookingUrl: "https://scheduler.zoom.us/megan-murray-1/followup-nutritionist",
//   },
//   "priscilla frattin": {
//     initialBookingUrl: "https://scheduler.zoom.us/priscilla-frattin/initial-nutritionist",
//     followUpBookingUrl: "https://scheduler.zoom.us/priscilla-frattin/followup-nutritionist",
//   },
//   // Care Guide only has a single link - same URL for initial and follow up.
//   "sarah harper": {
//     initialBookingUrl: "https://scheduler.zoom.us/sarah-harper-care-guide",
//     followUpBookingUrl: "https://scheduler.zoom.us/sarah-harper-care-guide",
//   },
//   "stacey dimock": {
//     initialBookingUrl: "https://scheduler.zoom.us/stacey-dimock/initial-clinician-inperson",
//     followUpBookingUrl: "https://scheduler.zoom.us/stacey-dimock/followup-clinician",
//   },
//   "zahraa bhimji": {
//     initialBookingUrl: "https://scheduler.zoom.us/zahraa-bhimji-np/initial-clinician",
//     followUpBookingUrl: "https://scheduler.zoom.us/zahraa-bhimji-np/followup-clinician",
//   },
// };
//
// // Keyed by the clinician's stable userRef (from Chat/GetCareGiverList - the
// // numeric userId from Patient/GetAllDoctorsAllTimeSlots is a separate join
// // and isn't guaranteed stable across endpoints). Prefer this over the
// // name-keyed table below once real userRefs are known - names can collide.
// const MOCK_SCHEDULER_LINKS_BY_PROVIDER = {
//   // "202505ABCD1234": {
//   //   initialBookingUrl: "https://scheduler.zoom.us/mfl-demo/stacey-initial",
//   //   followUpBookingUrl: "https://scheduler.zoom.us/mfl-demo/stacey-followup",
//   // },
// };
//
// // Last-resort fallback so every category always has *some* clickable link
// // during frontend dev/QA, for any provider not covered above.
// const MOCK_SCHEDULER_LINKS_BY_CATEGORY = {
//   clinician: {
//     initialBookingUrl: "https://scheduler.zoom.us/mfl-demo/clinician-initial",
//     followUpBookingUrl: "https://scheduler.zoom.us/mfl-demo/clinician-followup",
//   },
//   "fertility-doula": {
//     initialBookingUrl: "https://scheduler.zoom.us/mfl-demo/fertility-doula-initial",
//     followUpBookingUrl: "https://scheduler.zoom.us/mfl-demo/fertility-doula-followup",
//   },
//   nutritionist: {
//     initialBookingUrl: "https://scheduler.zoom.us/mfl-demo/nutritionist-initial",
//     followUpBookingUrl: "https://scheduler.zoom.us/mfl-demo/nutritionist-followup",
//   },
// };
//
// function realLinksForProvider(provider) {
//   const perProvider = provider?.userRef ? MOCK_SCHEDULER_LINKS_BY_PROVIDER[provider.userRef] : null;
//   const byName = provider?.name ? SCHEDULER_LINKS_BY_NAME[normalizeName(provider.name)] : null;
//   return perProvider || byName || null;
// }
//
// /**
//  * @param {{ userRef?: string, name?: string }} provider  clinician record from clinicianDirectory.js
//  * @param {string} categoryId              PROVIDER_CATEGORIES[n].id
//  * @param {boolean} isFollowUp
//  * @param {{ id?: string|number, firstName?: string, lastName?: string, email?: string }} [patient]
//  * @returns {string|null} the Zoom Scheduler URL to open, or null if none is configured
//  */
// export function getSchedulerUrlForProvider(provider, categoryId, isFollowUp, patient) {
//   const links = realLinksForProvider(provider) || MOCK_SCHEDULER_LINKS_BY_CATEGORY[categoryId] || null;
//   if (!links) return null;
//   const base = (isFollowUp ? links.followUpBookingUrl : links.initialBookingUrl) || null;
//   return withPatientReferenceParams(base, patient);
// }
//
// /**
//  * Whether this provider has a *real* mapped scheduler link (not the
//  * category-level demo fallback).
//  * @param {{ userRef?: string, name?: string }} provider
//  * @returns {boolean}
//  */
// export function hasSchedulerUrlForProvider(provider) {
//   return Boolean(realLinksForProvider(provider));
// }

/**
 * Zoom Scheduler prefills a booking form from URL query params, as long as
 * the param name exactly matches the form field's name (per Zoom's docs,
 * support.zoom.com KB0084432).
 *
 * `email` is the piece that actually matters: the backend
 * (GenericService.CreateSchedulerAppointmentAndDebitAsync) resolves which
 * patient made a booking by matching the Zoom registrant's email to a User
 * row - there is no separate reference-id mechanism it reads. If the patient
 * edits this prefilled email before submitting on Zoom's page, the match
 * fails and the booking is flagged "Unresolved" for manual review; there is
 * currently no retry/fallback path for that case.
 *
 * Applied to whatever URL the caller has - clinician.initialBookingLink or
 * followUpBookingLink from the backend, or a hardcoded link for Care Guide.
 */
export function withPatientReferenceParams(url, patient) {
  if (!url || !patient) return url;
  const params = new URLSearchParams();
  if (patient.firstName) params.set("firstname", patient.firstName);
  if (patient.lastName) params.set("lastname", patient.lastName);
  if (patient.email) params.set("email", patient.email);
  if (patient.id != null) params.set("utm_content", String(patient.id));
  const qs = params.toString();
  if (!qs) return url;
  return `${url}${url.includes("?") ? "&" : "?"}${qs}`;
}
